import { getAuth } from './auth';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { getConstantsFromCache, ConstantsType } from './hooks/useConstants';
import {
  clearGraphData,
  setActiveGraph,
  setBoxData,
  setGraphData,
  setInterval,
  setIsLoading,
  setPrevEndTime,
  setPrevStartTime,
  setScatterData
} from 'redux/slice/graphControllerSlice';
import api from 'utils/api';
import { processBoxData } from './data';
import { APP_VERSION } from 'constants';

export const getAppVersion = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const date = now.getDate();
  const dateStr = `${now.getFullYear()}${month < 10 ? '0' + month : month}${
    date < 10 ? '0' + date : date
  }`;
  const BUILD_NUM = import.meta.env.VITE_BUILD_NUM || '';

  return BUILD_NUM ? ` v${APP_VERSION}-(${dateStr}-${BUILD_NUM})` : '';
};

const getOrCreateTooltip = (chart: any) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chartjs-tooltip';

    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

export const myChartJsTooltipHandler = (context: any, htmlGenerator: any) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  const yAlign = tooltip.yAlign;
  const xAlign = tooltip.xAlign;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set caret Position
  tooltipEl.classList.remove('top', 'bottom', 'center', 'left', 'right');
  // if (tooltipModel.yAlign || tooltipModel.xAlign) {
  tooltipEl.classList.add(yAlign);
  tooltipEl.classList.add(xAlign === 'center' ? 'left' : xAlign);

  // Set Text
  const body = htmlGenerator(tooltip);
  tooltipEl.innerHTML = body;

  // Tooltip height and width
  const { height, width } = tooltipEl.getBoundingClientRect();
  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
  // Carets
  const caretY = tooltip.caretY;
  const caretX = tooltip.caretX;

  // Final coordinates
  let top = positionY + caretY - height;
  let left = positionX + caretX - width / 2;
  let space = 8; // The caret plus one pixle for some space, you can increase it.

  // yAlign could be: `top`, `bottom`, `center`
  if (yAlign === 'top') {
    top += height + space;
  } else if (yAlign === 'center') {
    top += height / 2;
  } else if (yAlign === 'bottom') {
    top -= space;
  }

  // xAlign could be: `left`, `center`, `right`
  if (xAlign === 'left') {
    left += width / 2 - space / 2;
    if (yAlign === 'center') {
      left += space * 2;
    }
  } else if (xAlign === 'right') {
    left -= width / 2;
    if (yAlign === 'center') {
      left -= space;
    } else {
      left += space;
    }
  } else if (xAlign === 'center') {
    left += width / 2 + space * 2;
  }

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = left + 'px';
  tooltipEl.style.top = top + 'px';

  // // Auto position tooltip, based on dimensions and boundary
  // const chartRect = chart.canvas.getBoundingClientRect();
  // const tooltipRect = tooltipEl.getBoundingClientRect();
  // if (tooltipRect.x + tooltipRect.width > chartRect.width) {
  //   console.log('Out of bounds');
  // }
};

export const fetchUserParams = async () => {
  const user = getAuth().user;
  if (user) {
    const params = await api.get(`/api/accessibleParameters/${user.userId}`);
    return params.data.map((param: any) => ({
      ...param
    }));
  }
  return {};
};

export const getQuantity = (quantity: any): string => {
  return quantity.toLowerCase().replace(/\s/g, '-');
};

export const truncateMarkdown = (markdown: any, maxLength: any) => {
  if (markdown.length <= maxLength) return markdown;

  // Find the last space within the maxLength
  const truncated = markdown.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  // Cut off at the last space and add ellipsis
  return `${truncated.slice(0, lastSpaceIndex)}...`;
};

export const getNearestDBInterval = (
  fromTimestamp: number,
  toTimestamp: number
): any => {
  const INTERVALS = getConstantsFromCache()?.INTERVALS!;
  const millisecondsInDay = 24 * 60 * 60 * 1000;
  const durationInDays = (toTimestamp - fromTimestamp) / millisecondsInDay;

  let nearestInterval = null;

  if (durationInDays <= 1) {
    nearestInterval = INTERVALS['5M'];
  } else if (durationInDays > 1 && durationInDays <= 3) {
    nearestInterval = INTERVALS['30M'];
  } else if (durationInDays > 3 && durationInDays <= 7) {
    nearestInterval = INTERVALS['1H'];
  } else if (durationInDays > 7 && durationInDays <= 50) {
    nearestInterval = INTERVALS['12H'];
  } else if (durationInDays > 50 && durationInDays <= 100) {
    nearestInterval = INTERVALS['1D'];
  } else if (durationInDays > 100 && durationInDays <= 300) {
    nearestInterval = INTERVALS['3D'];
  } else if (durationInDays > 300 && durationInDays <= 800) {
    nearestInterval = INTERVALS['1W'];
  } else {
    nearestInterval = INTERVALS['1MO'];
  }

  return nearestInterval;
};

export const queryData = async (
  startTime: any,
  endTime: any,
  card: any,
  interval: string,
  dontShowGlobalLoader: boolean = true,
  url: string
) => {
  const { LOCATION_FULL_FORM_TO_ACRONYM_MAP } =
    getConstantsFromCache() as ConstantsType;
  const config: any = {
    method: 'post',
    url: `${import.meta.env.VITE_API_URL}${url}`,
    data: {
      parameterName: card.parameterName,
      before: endTime,
      after: startTime,
      parameterId: card.id.toString(),
      depth: card.depth.value,
      location: LOCATION_FULL_FORM_TO_ACRONYM_MAP[card.location],
      interval: card.isCruiseParam || card.isMonthlyAvg ? '1MO' : interval
    },
    accuracy: card.noOfDecimalPlaces,
    dontShowGlobalLoader
  };
  const response: any = await api(config);
  // @ts-ignore
  return {
    data: response.data,
    accuracy: response.config.accuracy,
    interval: config.data.interval,
    isCruiseParam: card.isCruiseParam
  };
};

export const useFetchGraphData = () => {
  const dispatch = useDispatch();

  const fetchGraphData = useCallback(
    async (
      startTime: number,
      endTime: number,
      cardSet: any[],
      isZoomCall = false
    ) => {
      let fetchedInitialData = false;
      try {
        const nextParams = cardSet.map((card: any) => card.parameterName);
        dispatch(setIsLoading(true));
        dispatch(clearGraphData());

        // Use one resolution for comparisons involving monthly catalogue entries.
        const monthlyOnly = cardSet.some(
          (card) => card.isMonthlyAvg || card.isCruiseParam
        );
        const interval = monthlyOnly
          ? '1MO'
          : getNearestDBInterval(startTime, endTime);
        const newData = await Promise.all(
          cardSet.map((card: any) =>
            queryData(
              startTime,
              endTime,
              card,
              interval,
              true,
              '/api/aggData/line'
            )
          )
        );

        dispatch(setInterval(interval));
        dispatch(setGraphData({ data: newData, nextParams }));
        dispatch(setActiveGraph('Line Chart'));
        dispatch(setPrevEndTime(endTime));
        dispatch(setPrevStartTime(startTime));
        fetchedInitialData = true;
      } catch (error: any) {
        console.error('Error fetching graph data:', error);
        alert(getDataErrorMessage(error));
      } finally {
        dispatch(setIsLoading(false));
      }

      if (!isZoomCall && fetchedInitialData) {
        try {
          const intervals = cardSet.some(
            (card) => card.isMonthlyAvg || card.isCruiseParam
          )
            ? ['1MO']
            : getNearestBinSizesForBoxPlot(startTime, endTime);

          const newData = await Promise.all(
            cardSet.map(async (card: any) => {
              const cardBoxData: any = {};
              for (const interval of intervals) {
                const res: any = await queryData(
                  startTime,
                  endTime,
                  card,
                  interval,
                  true,
                  '/api/aggData/box'
                );
                cardBoxData[interval] = processBoxData(
                  card.isCruiseParam,
                  res,
                  interval
                );
              }
              return cardBoxData;
            })
          );
          dispatch(setBoxData(newData));
        } catch (error) {
          console.error('Error fetching box data:', error);
          alert(getDataErrorMessage(error));
        }
      }

      if (!isZoomCall && fetchedInitialData) {
        try {
          const interval = cardSet.some(
            (card) => card.isMonthlyAvg || card.isCruiseParam
          )
            ? '1MO'
            : '1D';
          const newData = await Promise.all(
            cardSet.map((card: any) =>
              queryData(
                startTime,
                endTime,
                card,
                interval,
                true,
                '/api/aggData/line'
              )
            )
          );
          dispatch(setScatterData(newData));
        } catch (error) {
          console.error('Error fetching scatter data:', error);
          alert(getDataErrorMessage(error));
        }
      }
    },
    [dispatch]
  );

  return { fetchGraphData };
};

export const getDataErrorMessage = (error: any): string => {
  if (error?.response?.status === 403) {
    return 'You do not have VIEW access to this dataset for the entire selected date range. Choose an allowed dataset and date range.';
  }
  return (
    error?.response?.data?.message || 'Unable to load data. Please try again.'
  );
};

export const getNearestBinSizesForBoxPlot = (
  fromTimestamp: number,
  toTimestamp: number
): any => {
  const millisecondsInDay = 24 * 60 * 60 * 1000;
  const durationInDays = (toTimestamp - fromTimestamp) / millisecondsInDay;
  const INTERVALS = getConstantsFromCache()?.INTERVALS!;
  let intervals;

  if (durationInDays <= 30) {
    intervals = [INTERVALS['1D'], INTERVALS['1W'], INTERVALS['1MO']];
  } else if (durationInDays > 30 && durationInDays <= 210) {
    intervals = [INTERVALS['1W'], INTERVALS['1MO']];
  } else {
    intervals = [INTERVALS['1MO']];
  }

  return intervals;
};
